'use client'

import { useEffect, useState, useCallback } from 'react'
import {
  Dialog,
  Navigation,
  Cell,
  Text,
  Button,
  Icon,
  Tooltip,
  Divider,
  Spacer,
} from '@vinted/web-ui'
import { Copy24, Envelope24, Home24, X24 } from '@vinted/monochrome-icons'
import { compact } from 'lodash'

import { useTranslate } from '@marketplace-web/shared/i18n'

import { getBusinessAccountInvoiceInstructions } from 'data/api/get-business-account-invoice-instructions'
import { transformBusinessAccountInvoiceInstructionsDto } from 'data/transformers/business-account'
import { BusinessAccountInvoiceInstructionsModel } from 'types/models/business-account'
import { AddressModel } from 'types/models/address'

type Props = {
  transactionId: number | undefined
  isOpen: boolean
  onClose: () => void
}

const BuyerDetailsModal = ({ transactionId, isOpen, onClose }: Props) => {
  const translate = useTranslate('conversation.buyer_details.modal')

  const [buyerDetails, setBuyerDetails] = useState<BusinessAccountInvoiceInstructionsModel | null>(
    null,
  )

  const fetchBuyerDetails = useCallback(async () => {
    if (!transactionId) return

    const response = await getBusinessAccountInvoiceInstructions({ transactionId })

    if ('errors' in response) return

    setBuyerDetails(
      transformBusinessAccountInvoiceInstructionsDto(
        response.business_account_invoice_instructions,
      ),
    )
  }, [transactionId])

  useEffect(() => {
    fetchBuyerDetails()
  }, [fetchBuyerDetails])

  const generateAddressText = ({ name, line1, line2, postalCode, city, country }: AddressModel) =>
    compact([name, line1, line2, postalCode, city, country]).join('\n')

  const handleTooltipClick = (text: string) => () => navigator.clipboard.writeText(text)

  const renderSuffix = (copyText: string) => (
    <Tooltip placement={Tooltip.Placement.Left} hover={false} content={translate('actions.copy')}>
      <Button
        icon={<Icon name={Copy24} color={Icon.Color.GreyscaleLevel3} />}
        styling={Button.Styling.Flat}
        onClick={handleTooltipClick(copyText)}
      />
    </Tooltip>
  )

  const renderAddress = () => {
    if (!buyerDetails) return null

    const { address } = buyerDetails
    const { name, line1, line2, postalCode, city, country } = address

    return (
      <div id="address-block">
        <Cell
          prefix={<Icon name={Home24} color={Icon.Color.GreyscaleLevel3} />}
          suffix={renderSuffix(generateAddressText(address))}
          title={translate('labels.address')}
        >
          <Text as="span" width={Text.Width.Parent}>
            {name}
          </Text>
          <Text as="span" width={Text.Width.Parent}>
            {line1}
          </Text>
          {line2 && (
            <Text as="span" width={Text.Width.Parent}>
              {line2}
            </Text>
          )}
          <Text as="span" width={Text.Width.Parent}>
            {postalCode}, {city}
          </Text>
          <Text as="span" width={Text.Width.Parent}>
            {country}
          </Text>
        </Cell>
      </div>
    )
  }

  const renderEmail = () => {
    if (!buyerDetails) return null

    const { email } = buyerDetails

    return (
      <Cell
        prefix={<Icon name={Envelope24} color={Icon.Color.GreyscaleLevel3} />}
        suffix={renderSuffix(email)}
        title={translate('labels.email')}
      >
        <div className="u-ellipsis">{email}</div>
      </Cell>
    )
  }

  return (
    <Dialog show={isOpen}>
      <Navigation
        right={<Button styling={Button.Styling.Flat} onClick={onClose} iconName={X24} inline />}
        body={translate('title')}
      />
      <Divider />
      {renderAddress()}
      {renderEmail()}
      <Spacer size={Spacer.Size.X4Large} />
    </Dialog>
  )
}

export default BuyerDetailsModal
